import dayjs from 'dayjs';
import { ReactNode, useEffect, useMemo, useState } from 'react';

import { Tooltip } from 'components/Tooltip';

const INTERVALS = {
  minute: 60,
  hour: 60 * 60,
  day: 60 * 60 * 24,
  month: 60 * 60 * 24 * 30,
  year: 60 * 60 * 24 * 365,
};
/**
 * Formats relative time as shorthand (e.g., "5m ago", "2h ago")
 */
function formatShorthand(date: dayjs.Dayjs, now: dayjs.Dayjs): string {
  const diffSeconds = now.diff(date, 'second');
  if (diffSeconds < INTERVALS.minute) return 'now';
  if (diffSeconds < INTERVALS.hour)
    return `${Math.floor(diffSeconds / INTERVALS.minute)}m ago`;
  if (diffSeconds < INTERVALS.day)
    return `${Math.floor(diffSeconds / INTERVALS.hour)}h ago`;
  if (diffSeconds < INTERVALS.month)
    return `${Math.floor(diffSeconds / INTERVALS.day)}d ago`;
  if (diffSeconds < INTERVALS.year) {
    const months = Math.floor(diffSeconds / INTERVALS.month);
    const remainingDays = Math.floor(
      (diffSeconds % INTERVALS.month) / INTERVALS.day
    );
    return `${months}mo ${remainingDays > 0 ? `${remainingDays}d ` : ''}ago`;
  }
  const years = Math.floor(diffSeconds / INTERVALS.year);
  const remainingMonths = Math.floor(
    (diffSeconds % INTERVALS.year) / INTERVALS.month
  );
  return `${years}y ${remainingMonths > 0 ? `${remainingMonths}mo ` : ''}ago`;
}

interface RelativeTimeProps {
  date: string | Date | dayjs.Dayjs | null | undefined;
  fallback?: string;
  /**
   * Update this data in real-time
   * @default true
   */
  realTimeUpdate?: boolean;
  /**
   * Minimum refresh cadence in ms (used as a floor for older timestamps).
   * For very recent timestamps we may refresh more frequently to keep "just now"
   * and "x seconds ago" feeling real-time.
   * @default 60000 (1 minute)
   */
  minUpdateInterval?: number;
  /**
   * Use shorthand format (e.g., "5m ago" instead of "5 minutes ago")
   * @default false
   */
  shorthand?: boolean;
  /**
   * Show a title tooltip with the full formatted date (lll format)
   * @default 'title'
   */
  showTitle?: 'tooltip' | 'title' | false;
  /**
   * Custom render function to format the date
   * @default undefined
   */
  customRender?: (dateString: string) => ReactNode;
}

/**
 * Component that displays relative time (e.g., "2 minutes ago") and updates in real-time
 * @param date - The date to display relative to now
 * @param fallback - Text to show if date is invalid or null
 * @param realTimeUpdate - Update this data in real-time
 * @param minUpdateInterval - Minimum refresh cadence in milliseconds
 * @param shorthand - Use shorthand format (e.g., "5m ago" instead of "5 minutes ago")
 * @param showTitle - Show a title tooltip with the full formatted date
 * @param customRender - Custom render function to format the date
 */
export function RelativeTime({
  date,
  fallback = '-',
  realTimeUpdate = true,
  minUpdateInterval = 60000, // 1 minute default
  shorthand = false,
  showTitle = 'title',
  customRender,
}: RelativeTimeProps) {
  const [nowMs, setNowMs] = useState(() => Date.now());

  const parsedDate = useMemo(() => {
    if (!date) return null;
    const value = dayjs(date);
    return value.isValid() ? value : null;
  }, [date]);

  useEffect(() => {
    if (!parsedDate || !realTimeUpdate) return;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let isCancelled = false;
    const getNextDelayMs = () => {
      const now = dayjs();
      const diffSeconds = now.diff(parsedDate, 'second');
      // For very recent timestamps, update on a 1 second boundary
      if (diffSeconds < 60) return 1000 - (Date.now() % 1000);
      // For timestamps under an hour, update on 30s boundaries
      if (diffSeconds < 60 * 60) {
        const interval = 30000;
        return interval - (Date.now() % interval);
      }
      // Older timestamps: minute-level is enough, but never sooner than minUpdateInterval
      const interval = Math.max(minUpdateInterval, 60000);
      return interval - (Date.now() % interval);
    };

    const tick = () => {
      if (isCancelled) return;
      setNowMs(Date.now());
      timeoutId = setTimeout(tick, getNextDelayMs());
    };
    // Schedule updates precisely when the label is expected to change.
    timeoutId = setTimeout(tick, getNextDelayMs());

    return () => {
      isCancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [parsedDate, minUpdateInterval, realTimeUpdate]);

  if (!parsedDate) return fallback;

  const now = dayjs(nowMs);
  const relativeText = shorthand
    ? formatShorthand(parsedDate, now)
    : parsedDate.from(now);

  const formattedDate = customRender
    ? customRender(relativeText)
    : relativeText;

  const tooltipTitle = parsedDate.format('lll z');

  if (showTitle === 'title') {
    return <span title={tooltipTitle}>{formattedDate}</span>;
  }

  if (showTitle === 'tooltip') {
    return <Tooltip title={tooltipTitle}>{formattedDate}</Tooltip>;
  }

  return formattedDate;
}
